import { useState, useCallback, useRef, useEffect } from 'react';

// ══════════════════════════════════════════════════════════════════════════════
// useProductCache — Secure, eager, multi-tenant product catalog cache
// ══════════════════════════════════════════════════════════════════════════════
//
// Key Architecture (two-layer isolation):
//   Layer 1: kangavena:cache:storeId:<storeApiKey>   → maps runtime apiKey → DB storeId
//   Layer 2: kangavena:cache:products:<storeId>      → stores the product JSON blob
//
// Multi-tenant guarantee: two different stores in the same browser can NEVER
// collide because every product key is scoped to the numeric DB storeId.
// storeApiKey is only used to derive Layer 1; the actual product data lives under
// the server-validated storeId so a leaked or guessed apiKey never exposes data.
//
// Boot flow (online):
//   1. commitStoreId(id)    → writes Layer 1 immediately after /api/stores resolves
//   2. writeCache(products) → writes Layer 2 after every successful /api/inventory fetch
//
// Boot flow (offline hard-refresh):
//   useState lazy init      → reads Layer 1 → reads Layer 2 synchronously, zero flash
//
// Race condition guard (first-ever session):
//   If /api/inventory resolves before /api/stores, Layer 1 doesn't exist yet.
//   writeCache() returns false.  The caller stores raw products in pendingInventoryRef.
//   When commitStoreId() fires, the caller flushes pendingInventoryRef via writeCache().
//
// Session clearance:
//   clearSessionCache()     → removes both Layer 1 and Layer 2 keys for this store
//                             + evicts any legacy-format keys from prior releases
// ══════════════════════════════════════════════════════════════════════════════

const STORE_ID_KEY = (apiKey: string)  => `kangavena:cache:storeId:${apiKey}`;
const PRODUCTS_KEY = (storeId: string) => `kangavena:cache:products:${storeId}`;
const LEGACY_KEY   = (apiKey: string)  => `kangavena:product_cache:${apiKey}`;

// ── Offline-safe storeId resolution ──────────────────────────────────────────
// On hard-refresh while offline, App.tsx's /api/my-store fetch fails, so
// storeApiKey prop arrives as ''.  We must still find the storeId to locate
// the product cache.  Resolution order (most → least reliable):
//   1. Layer 1 key (set by commitStoreId when last online — best case)
//   2. pos_cashier_session.store.id  (written at login, always present for /pos/cashier)
//   3. pos_admin_session.storeId     (written at login, always present for /pos/admin)
// Multi-tenant guarantee: the session storeId is server-validated — a different
// tenant logging in will overwrite the session with their own storeId.
function resolveStoreId(apiKey: string): string | null {
  // 1. Explicit Layer-1 mapping (committed when last online)
  if (apiKey) {
    const id = localStorage.getItem(STORE_ID_KEY(apiKey));
    if (id) return id;
  }
  // 2. Cashier session (pos_cashier_session → store.id)
  try {
    const raw = localStorage.getItem('pos_cashier_session');
    if (raw) {
      const s = JSON.parse(raw);
      if (s?.store?.id) return String(s.store.id);
    }
  } catch { /* malformed */ }
  // 3. Admin session (pos_admin_session → storeId)
  try {
    const raw = localStorage.getItem('pos_admin_session');
    if (raw) {
      const s = JSON.parse(raw);
      if (s?.storeId) return String(s.storeId);
    }
  } catch { /* malformed */ }
  return null;
}

export function useProductCache<T>(
  storeApiKey: string,
  transform: (raw: any[]) => T[],
) {
  // Keep transform current without invalidating memoised callbacks on each render.
  // Since callers typically pass a module-level function this is just a safety net.
  const transformRef = useRef(transform);
  transformRef.current = transform;

  // Lazy init: resolves storeId from Layer 1 → loads Layer 2 synchronously before
  // the first render.  The product grid is never blank on an offline hard-refresh.
  const [inventory, setInventory] = useState<T[]>(() => {
    try {
      // resolveStoreId works even when storeApiKey is '' (offline hard-refresh)
      const storeId = resolveStoreId(storeApiKey);
      if (!storeId) return [];
      const raw = localStorage.getItem(PRODUCTS_KEY(storeId));
      if (!raw) return [];
      const parsed = JSON.parse(raw) as any[];
      return Array.isArray(parsed) && parsed.length > 0
        ? transformRef.current(parsed)
        : [];
    } catch {
      return [];
    }
  });

  // Persist the DB storeId so the lazy init above can resolve it on the next offline
  // mount.  Call this immediately after /api/stores resolves and setStoreId fires.
  const commitStoreId = useCallback((id: string): void => {
    try { localStorage.setItem(STORE_ID_KEY(storeApiKey), id); } catch { /* quota */ }
  }, [storeApiKey]);

  // Write the full raw (pre-transform) catalog to cache after each successful fetch.
  // Resolution order for the storeId write-key:
  //   1. Layer 1 (committed by commitStoreId after /api/stores resolves) — normal case
  //   2. resolveStoreId() session fallback — handles the window where storeApiKey is ''
  //      (apiKey not yet populated in React state while /api/my-store is in-flight).
  //      Without this fallback writeCache always returns false when storeApiKey='' and
  //      products land in pendingInventoryRef but the /api/stores flush also fails
  //      (can't match st.apiKey===''), so products are NEVER persisted to the new-format
  //      cache key and every subsequent offline boot shows an empty grid.
  const writeCache = useCallback((rawProducts: any[]): boolean => {
    try {
      let id = storeApiKey
        ? localStorage.getItem(STORE_ID_KEY(storeApiKey))
        : null;
      // Fallback: resolve from session when Layer 1 is not yet committed
      if (!id) id = resolveStoreId(storeApiKey);
      if (!id) return false;
      const sanitized = rawProducts.map(p => {
        if (p.productImage && typeof p.productImage === 'string' && p.productImage.startsWith('data:')) {
          return { ...p, productImage: `/api/products/${p.id}/image` };
        }
        return p;
      });
      localStorage.setItem(PRODUCTS_KEY(id), JSON.stringify(sanitized));
      return true;
    } catch {
      return false;
    }
  }, [storeApiKey]);

  // Hydrate inventory state from cache (called when the network fetch fails).
  // Self-resolving: reads storeId from Layer 1 so it works even when storeId
  // state in the component is still null (offline mount).
  const readCache = useCallback((): boolean => {
    try {
      // resolveStoreId works even when storeApiKey is '' (offline hard-refresh)
      const id = resolveStoreId(storeApiKey);
      if (!id) return false;
      const raw = localStorage.getItem(PRODUCTS_KEY(id));
      if (!raw) return false;
      const parsed = JSON.parse(raw) as any[];
      if (Array.isArray(parsed) && parsed.length > 0) {
        setInventory(transformRef.current(parsed));
        return true;
      }
    } catch { /* corrupt cache — ignore */ }
    return false;
  }, [storeApiKey]);

  // Remove all cache keys for this store.  Call on explicit logout or store switch
  // so a subsequent session never sees stale data from a different tenant.
  const clearSessionCache = useCallback((): void => {
    try {
      const id = localStorage.getItem(STORE_ID_KEY(storeApiKey));
      if (id) localStorage.removeItem(PRODUCTS_KEY(id));
      localStorage.removeItem(STORE_ID_KEY(storeApiKey));
      localStorage.removeItem(LEGACY_KEY(storeApiKey));
    } catch { /* ignore */ }
  }, [storeApiKey]);

  // Auto-hydrate from cache when storeApiKey is provided or updated
  useEffect(() => {
    if (storeApiKey) {
      readCache();
    }
  }, [storeApiKey, readCache]);

  return { inventory, setInventory, commitStoreId, writeCache, readCache, clearSessionCache };
}
